import { CAMERA_GRAPH_DIAGNOSTICS as style } from '../_data/cameraGraphDiagnostics.js';
export function cameraGraphDiagnosticsKey(space, kind, id) {
    return `camera-graph-diagnostics:${encodeURIComponent(space)}:${kind}:${encodeURIComponent(id)}`;
}
export function cameraGraphDiagnosticsTargetEndpoint(anchor) {
    if (anchor.anchorKind === 'item') {
        return { endpointKind: 'static', itemKey: anchor.anchorItemKey, anchorKey: 'center' };
    }
    if (anchor.anchorKind === 'parametric') {
        const { anchorKind: _anchorKind, ...endpoint } = anchor;
        return { endpointKind: 'parametric', ...endpoint };
    }
    return undefined;
}
/** Only the renderer receives this frame. Its domain records remain shared and untouched. */
export function projectCameraGraphDiagnosticsFrame(frame, graph, activeView, positions) {
    const itemCollections = [...frame.itemCollections];
    const constellations = [...frame.constellations];
    const entityItemByKey = { ...frame.itemParametricState?.entityItemByKey };
    const connectorItemByKey = { ...frame.itemParametricState?.connectorItemByKey };
    const entityConstellationByKey = { ...frame.constellationParametricState?.entityConstellationByKey };
    const connectorConstellationByKey = { ...frame.constellationParametricState?.connectorConstellationByKey };
    const key = (kind, id) => cameraGraphDiagnosticsKey(frame.spaceKey, kind, id);
    const stationItemKey = (id) => `item:entity:${key('station', id)}`;
    const marker = (id, itemKey, position, name, color) => {
        const collectionKey = `collection:entity:${id}`;
        const constellationKey = `constellation:entity:${id}`;
        itemCollections.push({ itemCollectionKind: 'entity', itemCollectionKey: collectionKey,
            items: [{ itemKind: 'entity', itemKey, name }] });
        constellations.push({ constellationKind: 'entity', constellationKey, itemCollectionKey: collectionKey,
            anchor: { itemKey, anchorKey: 'center' } });
        entityConstellationByKey[constellationKey] = {
            layout: { layoutKind: 'spoke', anchor: { anchorKind: 'origin' }, originNearestPosition: position,
                direction: [1, 0, 0], spacing: 0 },
        };
        const state = {
            shape: { shapeKind: 'ring-guide', labelHostCapability: 'above-only',
                radius: style.markerRadius, segments: 24,
                anchorsByKey: { center: { anchorKey: 'center', localPosition: [0, 0, 0] } } },
            interaction: { enabled: false },
            visualStyle: { color, opacity: 0.75 },
            cameraProximityFade: style.fade,
            label: { aboveItem: { text: name, fontSize: 0.18, color,
                    placement: { placementKind: 'item-origin', offset: [0, 0.4, 0] },
                    orientation: { orientationKind: 'billboard' } } },
        };
        entityItemByKey[itemKey] = state;
    };
    for (const [id, station] of Object.entries(graph.stations)) {
        const position = positions.get(id);
        if (!position)
            throw new Error(`Missing diagnostic camera station position: ${id}`);
        marker(key('station', id), stationItemKey(id), position, `${id} (${station.stationKind})`, graph.views[activeView ?? '']?.station === id ? style.activeColor : style.color);
    }
    const lines = [];
    const endpoint = (id) => ({ endpointKind: 'static', itemKey: stationItemKey(id), anchorKey: 'center' });
    const line = (kind, id, from, to, active) => {
        const itemKey = `item:connector:${key(kind, id)}`;
        lines.push({ itemKind: 'connector', itemKey, shape: { shapeKind: 'line-guide', labelHostCapability: 'above-only',
                start: [0, 0, 0], end: [0, 0, 0], anchorsByKey: {
                    start: { anchorKey: 'start', localPosition: [0, 0, 0] },
                    mid: { anchorKey: 'mid', localPosition: [0, 0, 0] },
                    end: { anchorKey: 'end', localPosition: [0, 0, 0] },
                } },
            endpoints: { from, to } });
        connectorItemByKey[itemKey] = {
            interaction: { enabled: false }, path: { pathKind: 'straight' },
            visualStyle: { color: active ? style.activeColor : style.color, opacity: active ? 0.85 : 0.25 },
            cameraNearEyeClipRadius: style.clipRadius,
        };
    };
    for (const [id, view] of Object.entries(graph.views)) {
        let target = cameraGraphDiagnosticsTargetEndpoint(view.target);
        if (!target) {
            const itemKey = `item:entity:${key('target', id)}`;
            const position = view.target.anchorKind === 'position' ? view.target.position : [0, 0, 0];
            marker(key('target', id), itemKey, position, `target: ${id}`, style.targetColor);
            target = { endpointKind: 'static', itemKey, anchorKey: 'center' };
        }
        line('view', id, endpoint(view.station), target, id === activeView);
    }
    for (const path of graph.paths) {
        line('path', path.pathId, endpoint(graph.views[path.from].station), endpoint(graph.views[path.to].station), path.to === activeView);
    }
    const collectionKey = `collection:connector:${key('lines', '')}`;
    const constellationKey = `constellation:connector:${key('lines', '')}`;
    itemCollections.push({ itemCollectionKind: 'connector', itemCollectionKey: collectionKey, items: lines });
    constellations.push({ constellationKind: 'connector', constellationKey, itemCollectionKey: collectionKey });
    connectorConstellationByKey[constellationKey] = {};
    return {
        ...frame, itemCollections, constellations,
        itemParametricState: { ...frame.itemParametricState, entityItemByKey, connectorItemByKey },
        constellationParametricState: { ...frame.constellationParametricState, entityConstellationByKey, connectorConstellationByKey },
    };
}
